import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getValueCaptureExportData,
  getValueCaptureFilters,
  getValueCaptureGraphData,
  getValueCaptureGraphDataSet2,
} from "../../queries/apiQueries";
import {
  SelectPicker,
  Notification,
  CheckPicker,
  Checkbox,
  useToaster,
  Button,
  Cascader,
  Whisper,
  Popover,
  Stack,
  Divider,
} from "rsuite";
import "./valueCapture.scss";
import { FILTERDATA } from "../../utils";
import { appInsights } from "../router";
import NetCostReductionChart from "./netCostReductionChart";
import MarginImprovement from "./marginImprovement";
import PricingExecution from "./pricingExecution";
import SalesGrowth from "./salesGrowth";
import NiaImprovements from "./niaImprovements";
import MarginValueCapture from "./marginValueCapture";
import { CSVLink } from "react-csv";
import Details from "./details";
import moment from "moment";
import MainHeader from "../../components/Layouts/MainHeader";
import MainContent from "../../components/Layouts/MainContent";
import { Loader } from "../../components/common/Loader";
import { OverlayTriggerHandle } from "rsuite/esm/internals/Picker";

type Year = 2024 | 2025 | 2026;

const baselineOptions = [2024, 2025] as const;
const currentOptions = [2025, 2026] as const;

const ValueCapture = (props: any) => {
  const whisperRef = useRef<OverlayTriggerHandle>(null);
  const popoverBodyRef = useRef<HTMLDivElement | null>(null);

  const baselineData = useMemo(
    () => [2024, 2025].map((y) => ({ label: String(y), value: y as Year })),
    [],
  );
  const currentData = useMemo(
    () => [2025, 2026].map((y) => ({ label: String(y), value: y as Year })),
    [],
  );

  const [baseline, setBaseline] = useState<Year | null>(null);
  const [current, setCurrent] = useState<Year | null>(null);

  const [timelineObject, setTimelineObject] = useState<{
    baseline: number;
    current: number;
  }>({ baseline: 2025, current: 2026 });

  const ready = baseline != null && current != null;
  const close = () => whisperRef.current?.close();

  const overlay = (
    <Popover title="Select years" style={{ width: 320 }}>
      <div ref={popoverBodyRef}>
        <Stack direction="column" spacing={10} alignItems="stretch">
          <div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Baseline (required)
            </div>
            <SelectPicker
              data={baselineData}
              value={baseline}
              onChange={setBaseline}
              placeholder="Select baseline year"
              searchable={false}
              block
              container={() => popoverBodyRef.current as HTMLElement}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Current (required)
            </div>
            <SelectPicker
              data={currentData}
              value={current}
              onChange={setCurrent}
              placeholder="Select current year"
              searchable={false}
              block
              container={() => popoverBodyRef.current as HTMLElement}
            />
          </div>

          <Divider style={{ margin: "6px 0" }} />
        </Stack>
      </div>
    </Popover>
  );

  const label = ready
    ? `Baseline ${baseline} • Current ${current}`
    : "Select years";

  const dispatch = useDispatch<any>();
  const toaster = useToaster();
  var isCleared = false;
  const footerStyles = {
    padding: "10px 2px",
    borderTop: "1px solid #e5e5e5",
  };

  const [filtersLoading, setFiltersLoading] = useState(true);
  const allFilters = useSelector(
    (state: any) => state.CommonReducer.allFilters,
  );

  const [graphData, setGraphData] = useState<Array<any>>([]);
  const [detailsData, setDetailsData] = useState<Array<any>>([]);
  const [goLiveDate, setGoLiveDate] = useState<Array<any>>([]);
  // const [avgReductionValue, setAvgReductionValue] = useState<Array<any>>([]);
  // const [yoyValue, setYoyValue] = useState(null);
  const [graphData2, setGraphData2] = useState<Array<any>>([]);
  const [detailsData2, setDetailsData2] = useState<Array<any>>([]);
  const [page, setPage] = useState("summary");
  const [isVisible, setIsVisible] = useState(false);
  const [isRightVisible, setIsRightVisible] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isGraphLoading, setIsGraphLoading] = useState(true);
  const [isGraph2Loading, setIsGraph2Loading] = useState(true);
  const [filterOptions, updateFilterOptions] = useState<any>({
    district: new Array<{ district: string }>(),
    supplier: new Array<{ supplier: string }>(),
    valueCapture_l3: new Array<{ valueCapture_l3: string }>(),
  });
  const [selectedFilters, setSelectedFilters] = useState<any>({
    ...allFilters,
    valueCapture_l3: [],
  });

  const [exportData, setExportData] = useState([]);
  const exportFullRef = useRef<any>(null);
  const didClear: any = useRef(false);
  const filtersMount: any = useRef(false);

  const token = useSelector((state: any) => state.CommonReducer.token);

  useEffect(() => {
    appInsights.trackPageView({
      name: `Navigate to Value Tracking by ${localStorage.getItem(
        "user_name",
      )}, ${localStorage.getItem("user_email")}`,
    });
    getFilters();
    getGraphData();
    getGraphDataSet2();
  }, []);

  useEffect(() => {
    if (
      !(
        (selectedFilters.valueCapture_l3.length === 1 &&
          selectedFilters.supplier.length === 0) ||
        (selectedFilters.supplier.length === 1 &&
          selectedFilters.valueCapture_l3.length === 0)
      )
    ) {
      setPage("summary");
    }
  }, [selectedFilters]);

  useEffect(() => {
    if (page == "details") {
      getGraphData("", "details");
      getGraphDataSet2("", "details");
    }
  }, [page]);

  const getGraphData = async (
    clearFilterFlag: any = null,
    currPage: any = "summary",
    filterSelected: any = null,
  ) => {
    let payload: any = {
      district: selectedFilters.district,
      valueCapture_l3: selectedFilters.valueCapture_l3,
      master_vendor_name: selectedFilters.supplier,
      baseline: baseline,
      current: current,
    };
    payload.isDetail = currPage == "details" ? true : false;

    if (clearFilterFlag == "valueCapture_l3") {
      payload.valueCapture_l3 = [];
      payload.master_vendor_name = [];
      payload.district = [];
    } else if (clearFilterFlag == "supplier") {
      payload.master_vendor_name = [];
      payload.district = [];
    } else if (clearFilterFlag == "district") {
      payload.district = [];
    }

    if (filterSelected == "valueCapture_l3") {
      payload.master_vendor_name = [];
      payload.district = [];
    } else if (filterSelected == "supplier") {
      payload.district = [];
    }

    let res = await dispatch(getValueCaptureGraphData(payload));
    if (payload.isDetail) {
      setDetailsData(res.data);
    } else {
      setGraphData(res.data);
    }
    setGoLiveDate(res.goliveDate);
    // setYoyValue(res.data.yoy);
    // setAvgReductionValue(res.data.averageReduction);
    setIsGraphLoading(false);
  };

  const getGraphDataSet2 = async (
    clearFilterFlag: any = null,
    currPage: any = "summary",
    filterSelected: any = null,
  ) => {
    let payload: any = {
      district: selectedFilters.district,
      valueCapture_l3: selectedFilters.valueCapture_l3,
      master_vendor_name: selectedFilters.supplier,
      baseline: baseline,
      current: current,
    };
    payload.isDetail = currPage == "details" ? true : false;

    if (clearFilterFlag == "valueCapture_l3") {
      payload.valueCapture_l3 = [];
      payload.master_vendor_name = [];
      payload.district = [];
    } else if (clearFilterFlag == "supplier") {
      payload.master_vendor_name = [];
      payload.district = [];
    } else if (clearFilterFlag == "district") {
      payload.district = [];
    }

    if (filterSelected == "valueCapture_l3") {
      payload.master_vendor_name = [];
      payload.district = [];
    } else if (filterSelected == "supplier") {
      payload.district = [];
    }

    let res = await dispatch(getValueCaptureGraphDataSet2(payload));

    if (payload.isDetail) {
      setDetailsData2(res);
    } else {
      setGraphData2(res);
    }
    setIsGraph2Loading(false);
  };

  /* Filters api call */
  const getFilters = async (
    payload: any = {},
    currentFilter: any = "all",
    fo: any = null,
  ) => {
    setFiltersLoading(true);
    let res = await dispatch(getValueCaptureFilters(payload));
    if (fo) {
      if (currentFilter === "valueCapture_l3") {
        updateFilterOptions({
          ...fo,
          supplier: [...res.master_vendor_name],
          district: [...res.district],
        });
      } else if (currentFilter === "supplier") {
        updateFilterOptions({
          ...fo,
          district: [...res.district],
        });
      } else if (currentFilter === "district") {
        updateFilterOptions({
          ...fo,
        });
      } else {
        updateFilterOptions({
          valueCapture_l3: res.valueCapture_l3,
          supplier: res.master_vendor_name,
          district: res.district,
        });
      }
    } else {
      if (currentFilter === "valueCapture_l3") {
        updateFilterOptions({
          ...filterOptions,
          supplier: [...res.master_vendor_name],
          district: [...res.district],
        });
      } else if (currentFilter === "supplier") {
        updateFilterOptions({
          ...filterOptions,
          district: [...res.district],
        });
      } else if (currentFilter === "district") {
        updateFilterOptions({
          ...filterOptions,
        });
      } else {
        updateFilterOptions({
          valueCapture_l3: res.valueCapture_l3,
          supplier: res.master_vendor_name,
          district: res.district,
        });
      }
    }

    setIsLoading(false);
    setFiltersLoading(false);
    filtersMount.current = false;
    return res;
  };

  /* Updating filter selection state  */
  const handleFilterUpdate = (value: any, filter: string) => {
    if (isCleared) {
      isCleared = false;
      return;
    }
    filtersMount.current = true;
    let x: string = filter;
    if (value === "all") {
      if (filterOptions?.[x].length === selectedFilters?.[x].length) {
        setSelectedFilters({ ...selectedFilters, [x]: [] });
      } else {
        if (x == "supplier") {
          setSelectedFilters({
            ...selectedFilters,
            [x]: [
              ...filterOptions?.[x].map(
                (option: any) => option.master_vendor_name,
              ),
            ],
          });
        } else {
          setSelectedFilters({
            ...selectedFilters,
            [x]: [...filterOptions?.[x].map((option: any) => option?.[x])],
          });
        }
      }
    } else {
      setSelectedFilters({ ...selectedFilters, [x]: [...value] });
    }
    didClear.current = true;
  };

  /* Updating filter options based on selection (api) */
  const handleClose = (filter: string) => {
    if (filter === "valueCapture_l3") {
      setSelectedFilters({
        ...selectedFilters,
        supplier: [],
        district: [],
      });
      getFilters(
        { ...selectedFilters, supplier: [], district: [] },
        "valueCapture_l3",
      );
      getGraphData("", "", "valueCapture_l3");
      getGraphDataSet2("", "", "valueCapture_l3");
    } else if (filter === "supplier") {
      setSelectedFilters({ ...selectedFilters, district: [] });
      getFilters({ ...selectedFilters, district: [] }, "supplier");
      getGraphData("", "", "supplier");
      getGraphDataSet2("", "", "supplier");
    } else if (filter === "district") {
      setSelectedFilters({ ...selectedFilters });
      getGraphData("", "", "district");
      getGraphDataSet2("", "", "district");
    }
    setIsGraphLoading(true);
    setIsGraph2Loading(true);
    // getGraphData();
    // getGraphDataSet2();
  };

  const handleClear = (filter: string) => {
    isCleared = true;

    didClear.current = true;
    if (filter === "valueCapture_l3") {
      setSelectedFilters({
        valueCapture_l3: [],
        supplier: [],
        district: [],
      });
      setIsGraphLoading(true);
      setIsGraph2Loading(true);
      getGraphData("valueCapture_l3");
      getGraphDataSet2("valueCapture_l3");
      getFilters(
        { valueCapture_l3: [], supplier: [], district: [] },
        "valueCapture_l3",
      );
      setPage("summary");
    } else if (filter === "supplier") {
      setSelectedFilters({
        ...selectedFilters,
        supplier: [],
        district: [],
      });
      setIsGraphLoading(true);
      setIsGraph2Loading(true);
      getGraphData("supplier");
      getGraphDataSet2("supplier");
      getFilters(
        { ...selectedFilters, supplier: [], district: [] },
        "supplier",
      );
      setPage("summary");
    } else if (filter === "district") {
      setIsGraphLoading(true);
      setIsGraph2Loading(true);
      getGraphData("district");
      getGraphDataSet2("district");
      setSelectedFilters({ ...selectedFilters, district: [] });
    }
  };

  const exportExcelHandler = async () => {
    toaster.push(message, { placement: "topCenter" });
    let payload = {
      district: selectedFilters.district,
      valueCapture_l3: selectedFilters.valueCapture_l3,
      master_vendor_name: selectedFilters.supplier,
      baseline:baseline,
      current:current
    };
    let res = await dispatch(getValueCaptureExportData(payload));
    res.data.map((item: any) => {
      item.gm2024 = item.gm2024 ? `${(item.gm2024 * 100)?.toFixed(2)}%` : 0;
      item.gm2025 = item.gm2025 ? `${(item.gm2025 * 100)?.toFixed(2)}%` : 0;
      item.costperunit2024 = `$${item.costperunit2024?.toFixed(2)}`;
      item.costperunit2025 = `$${item.costperunit2025?.toFixed(2)}`;
      item.netcost2024 = `$${item.netcost2024?.toFixed(0)}`;
      item.netcost2025 = `$${item.netcost2025?.toFixed(0)}`;
      item.sellpriceperunit2024 = `$${item.sellpriceperunit2024?.toFixed(2)}`;
      item.sellpriceperunit2025 = `$${item.sellpriceperunit2025?.toFixed(2)}`;
      item.revenue2025 = `$${item.revenue2025?.toFixed(0)}`;
      item.golive_date = moment(item.golive_date).format("YYYY-MM-DD");
      item.week_start_month = moment(item.week_start_month).format(
        "YYYY-MM-DD",
      );
      item.marginRevenue = `$${item?.marginRevenue || 0}`;
    });

    setExportData(res.data);
  };

  const csvHeaders = [
    { label: "Week start date", key: "week_start_month" },
    { label: "Vendor", key: "master_vendor_name" },
    { label: "Category", key: "valueCapture_l3" },
    { label: "Go-Live Date", key: "golive_date" },
    { label: "Cost ($/Unit) 2024", key: "costperunit2024" },
    { label: "Cost ($/Unit) 2025", key: "costperunit2025" },
    { label: "Qty 2025", key: "qty2025" },
    { label: "Net cost'24", key: "netcost2024" },
    { label: "Net cost'25", key: "netcost2025" },
    { label: "Sell price ($/Unit) 2024", key: "sellpriceperunit2024" },
    { label: "Sell price ($/Unit) 2025", key: "sellpriceperunit2025" },
    { label: "Revenue 2025", key: "revenue2025" },
    { label: "Gross Margin% 2024", key: "gm2024" },
    { label: "Gross Margin% 2025", key: "gm2025" },
    { label: "Margin Improvement Benefit", key: "marginRevenue" },
  ];

  useEffect(() => {
    if (exportData?.length !== 0) {
      exportFullRef?.current?.link.click();
    }
  }, [exportData]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <div className="valueCapture-container w-full ">
        <MainHeader
          onVisibilityChange={setIsVisible}
          isRightVisible={isRightVisible}
          setIsRightVisible={setIsRightVisible}
          topContent={
            <div className="flex justify-between items-center w-full">
              <div className="text-left pageline">
                <span className="pagelinestart">Value Tracking</span>{" "}
                <span className="pagelineslash">/</span>{" "}
                <span className="pagelinesub">
                  Category Strategy Implementation
                </span>
              </div>
              <Button
                className="nav-items flex bg-[#85bc23] text-white  px-4 rounded-[4px] h-[4vh] py-1 mt-1"
                onClick={exportExcelHandler}
              >
                Export
              </Button>
              <CSVLink
                ref={exportFullRef}
                data={exportData}
                headers={csvHeaders}
                filename="value_tracking.csv"
                className="hidden"
                target="_blank"
              ></CSVLink>
            </div>
          }
          bottomContent={
            <>
              <div className="sub-heading-text font-bold flex flex-col items-start">
                <span className="filterHead">Timeline</span>
                <Whisper
                  trigger="click"
                  placement="bottomStart"
                  speaker={overlay}
                  onClose={() => {
                    if (baseline && current) {
                      setIsGraphLoading(true);
                      setIsGraph2Loading(true);
                      getGraphData();
                      getGraphDataSet2();
                      setTimelineObject({baseline:baseline,current:current})
                    }
                  }}
                >
                  <Button className="timeline_button" appearance="default">
                    {label}
                  </Button>
                </Whisper>
              </div>
              {["valueCapture_l3", "supplier", "district"].map(
                (filterEntity) => (
                  <div
                    className="sub-heading-text font-bold flex flex-col items-start"
                    key={filterEntity}
                  >
                    <span className="filterHead">
                      {filterEntity === "valueCapture_l3"
                        ? "Category"
                        : filterEntity}
                    </span>
                    <CheckPicker
                      data={
                        filterOptions[filterEntity].length
                          ? filterOptions[filterEntity]
                          : []
                      }
                      placeholder="All"
                      labelKey={
                        filterEntity === "supplier"
                          ? "master_vendor_name"
                          : filterEntity
                      }
                      valueKey={
                        filterEntity === "supplier"
                          ? "master_vendor_name"
                          : filterEntity
                      }
                      placement={
                        filterEntity === "district"
                          ? "bottomEnd"
                          : "bottomStart"
                      }
                      value={
                        selectedFilters[filterEntity]
                          ? selectedFilters[filterEntity]
                          : []
                      }
                      onClose={() =>
                        filtersMount.current ? handleClose(filterEntity) : ""
                      }
                      onClean={() => handleClear(filterEntity)}
                      onChange={(value) =>
                        handleFilterUpdate(value, filterEntity)
                      }
                      loading={filtersLoading}
                      renderExtraFooter={() => (
                        <div style={footerStyles}>
                          <Checkbox
                            indeterminate={
                              selectedFilters[filterEntity].length > 0 &&
                              selectedFilters[filterEntity].length <
                                filterOptions[filterEntity].length
                            }
                            checked={
                              selectedFilters[filterEntity].length ===
                              filterOptions[filterEntity].length
                            }
                            onChange={(value) =>
                              handleFilterUpdate("all", filterEntity)
                            }
                          >
                            Check all
                          </Checkbox>
                        </div>
                      )}
                    />
                  </div>
                ),
              )}
            </>
          }
        ></MainHeader>
        <MainContent isVisible={isVisible} fullHeight={true}>
          <div
            className={`contentBox flex flex-col justify-start gap-y-[1vh] ${isVisible ? "overflow-x-auto" : "overflow-hidden"} h-[100%]`}
          >
            {isGraphLoading || isGraph2Loading ? (
              <Loader />
            ) : page == "summary" ? (
              <div className="flex flex-col gap-[1vh] h-full">
                <div className="flex-1 flex gap-[1vh]">
                  {
                    <NetCostReductionChart
                      chartData={graphData}
                      goLiveDate={goLiveDate}
                      heading={"Net Cost Reduction"}
                      // avgReductionValue={avgReductionValue}
                      page={page}
                      dateRange={timelineObject}
                    />
                  }
                  <PricingExecution
                    chartData={graphData}
                    heading={"Pricing Execution (Sell Price Index)"}
                    dateRange={timelineObject}
                  />
                  <MarginImprovement
                    chartData={graphData2}
                    goLiveDate={goLiveDate}
                    heading={"Margin Improvement"}
                    page={page}
                    dateRange={timelineObject}
                  />
                </div>
                <div className="flex-1 flex gap-[1vh]">
                  <SalesGrowth
                    chartData={graphData}
                    heading={"Sales Growth"}
                    page={page}
                    dateRange={timelineObject}
                  />
                  <NiaImprovements
                    chartData={graphData2}
                    heading={"NIA Improvements"}
                    dateRange={timelineObject}
                  />
                  <MarginValueCapture
                    chartData={graphData2}
                    heading={"Margin Value Capture"}
                    page={page}
                  />
                </div>
              </div>
            ) : (
              <Details
                graphData={detailsData}
                goLiveDate={goLiveDate}
                graphData2={detailsData2}
                page={page}
              />
            )}
          </div>
        </MainContent>
      </div>
    </>
  );
};

const message = (
  <Notification type={"info"} header={`Exporting...`} closable>
    <p>Your file is being created. It will be download automatically</p>
  </Notification>
);

const mapDispatchToProps = (dispatch: any) => ({
  onFilterChange: (data: any) => dispatch({ type: FILTERDATA, data: data }),
});
export default connect(null, mapDispatchToProps)(ValueCapture);
